const EVENT_ID = "explorer_eventId";
const EVENT_CODE = "explorer_eventCode";
const FLOW = "explorer_flow";
const LEAD = "explorer_lead_profile";
const PENDING = "explorer_pending_onboarding";
const LOCAL_PROFILE = "explorer_local_profile";
/** Set after step 2 successfully calls the API so checkout skips duplicate saves. */
const STEP2_PERSISTED = "explorer_step2_persisted";

export type ExplorerFlow = "event" | "mentor" | "mentee" | "venture";

export function setExplorerContext(
  eventId: string | null,
  flow: ExplorerFlow,
  eventCode?: string | null,
) {
  if (eventId) sessionStorage.setItem(EVENT_ID, eventId);
  else sessionStorage.removeItem(EVENT_ID);
  if (eventCode) sessionStorage.setItem(EVENT_CODE, eventCode);
  else if (eventCode === null) sessionStorage.removeItem(EVENT_CODE);
  sessionStorage.setItem(FLOW, flow);
}

export function getExplorerEventId(): string | null {
  return sessionStorage.getItem(EVENT_ID);
}

export function getExplorerEventCode(): string | null {
  return sessionStorage.getItem(EVENT_CODE);
}

export function getExplorerFlow(): ExplorerFlow {
  const f = sessionStorage.getItem(FLOW);
  if (f === "mentor" || f === "mentee" || f === "venture" || f === "event") return f;
  return "event";
}

/** Maps marketing entrySource (URL) to explorer flow for onboarding steps after login. */
export function mapEntrySourceToExplorerFlow(entrySource: string): ExplorerFlow {
  const s = entrySource.trim().toLowerCase();
  if (s === "mentorship" || s === "mentor") return "mentor";
  if (s === "mentee") return "mentee";
  if (s === "venture" || s === "venture_builder") return "venture";
  return "event";
}

export const EXPLORER_PENDING_SUCCESS_KEY = "explorer_pending_success";

export type ExplorerLeadProfile = {
  firstName: string;
  lastName: string;
  email: string;
};

export type ExplorerPendingOnboarding = {
  segment?: string;
  discoveryAnswers?: Record<string, string>;
};

/** Step 1 wizard data — session buffer until step 2 submits, then sent via Users unpaid-member profile API. */
export type ExplorerLocalProfile = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  city?: string;
  /** Separate from city (same as registration PersonalInfoStep). */
  country?: string;
  jobTitle?: string;
  companyName?: string;
  sector?: string;
  entrySource: string;
  eventId?: number | null;
  gdprConsent: boolean;
  marketingConsent?: boolean;
};

export function setExplorerLocalProfile(profile: ExplorerLocalProfile) {
  sessionStorage.setItem(LOCAL_PROFILE, JSON.stringify(profile));
  sessionStorage.removeItem(STEP2_PERSISTED);
}

export function setExplorerStep2Persisted() {
  sessionStorage.setItem(STEP2_PERSISTED, "1");
}

export function getExplorerStep2Persisted(): boolean {
  return sessionStorage.getItem(STEP2_PERSISTED) === "1";
}

export function getExplorerLocalProfile(): ExplorerLocalProfile | null {
  const raw = sessionStorage.getItem(LOCAL_PROFILE);
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Partial<ExplorerLocalProfile>;
    if (!p.email || typeof p.email !== "string") return null;
    return {
      firstName: (p.firstName ?? "").trim(),
      lastName: (p.lastName ?? "").trim(),
      email: p.email.trim(),
      phoneNumber: p.phoneNumber,
      city: p.city,
      country: p.country,
      jobTitle: p.jobTitle,
      companyName: p.companyName,
      sector: p.sector,
      entrySource: (p.entrySource ?? "event").trim() || "event",
      eventId: p.eventId ?? undefined,
      gdprConsent: Boolean(p.gdprConsent),
      marketingConsent: Boolean(p.marketingConsent),
    };
  } catch {
    return null;
  }
}

export function clearExplorerLocalProfile() {
  sessionStorage.removeItem(LOCAL_PROFILE);
}

export function setExplorerLeadProfile(profile: ExplorerLeadProfile) {
  sessionStorage.setItem(LEAD, JSON.stringify(profile));
}

export function getExplorerLeadProfile(): ExplorerLeadProfile | null {
  const raw = sessionStorage.getItem(LEAD);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ExplorerLeadProfile>;
    if (!parsed.email) return null;
    return {
      firstName: parsed.firstName ?? "",
      lastName: parsed.lastName ?? "",
      email: parsed.email,
    };
  } catch {
    return null;
  }
}

export function setExplorerPendingOnboarding(pending: ExplorerPendingOnboarding) {
  sessionStorage.setItem(PENDING, JSON.stringify(pending));
}

export function getExplorerPendingOnboarding(): ExplorerPendingOnboarding | null {
  const raw = sessionStorage.getItem(PENDING);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ExplorerPendingOnboarding;
  } catch {
    return null;
  }
}

export function clearExplorerPendingOnboarding() {
  sessionStorage.removeItem(PENDING);
}

/** After “Join membership”, event id to combine with membership checkout on `/register-user` (Explorer event+membership combo). */
export const EXPLORER_POST_MEMBERSHIP_EVENT_KEY = "explorer_post_membership_event";
const EXPLORER_POST_MEMBERSHIP_EVENT_PERSISTED_KEY =
  "explorer_post_membership_event_persisted";

/**
 * Set when membership payment save succeeds so RegisterUser can reopen on step 5 after remount.
 * Must be cleared when starting a new Explorer → membership checkout or a stale flag shows Confirmation before Personal Info.
 */
export const REGISTRATION_SHOW_CONFIRMATION_KEY = "registration_show_confirmation";
/** Set when Explorer post-membership event payment succeeds in the registration checkout flow. */
export const EXPLORER_EVENT_PAYMENT_DONE_KEY = "explorer_event_payment_done";

export const EXPLORER_COMBO_REGISTER_CHROME_EVENT = "smbf-explorer-combo-register-chrome";

type PersistedExplorerMembershipCombo = {
  eventId: string;
  ownerEmail?: string;
};

export function setExplorerPostMembershipEventCombo(
  eventId: string,
  ownerEmail?: string,
): void {
  const id = eventId.trim();
  if (!id) return;
  sessionStorage.setItem(EXPLORER_POST_MEMBERSHIP_EVENT_KEY, id);
  try {
    const payload: PersistedExplorerMembershipCombo = {
      eventId: id,
      ownerEmail: ownerEmail?.trim() || undefined,
    };
    localStorage.setItem(
      EXPLORER_POST_MEMBERSHIP_EVENT_PERSISTED_KEY,
      JSON.stringify(payload),
    );
  } catch {
    /* ignore blocked storage */
  }
}

export function getExplorerPostMembershipEventCombo(): string | null {
  const inSession = sessionStorage
    .getItem(EXPLORER_POST_MEMBERSHIP_EVENT_KEY)
    ?.trim();
  if (inSession) return inSession;
  try {
    const raw = localStorage.getItem(EXPLORER_POST_MEMBERSHIP_EVENT_PERSISTED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedExplorerMembershipCombo;
    const id = parsed?.eventId?.trim() || "";
    if (!id) return null;
    const ownerEmail = parsed?.ownerEmail?.trim().toLowerCase();
    const authEmail = localStorage.getItem("auth_email")?.trim().toLowerCase();
    if (ownerEmail && authEmail && ownerEmail !== authEmail) return null;
    return id;
  } catch {
    return null;
  }
}

export function clearExplorerPostMembershipEventCombo(): void {
  sessionStorage.removeItem(EXPLORER_POST_MEMBERSHIP_EVENT_KEY);
  try {
    localStorage.removeItem(EXPLORER_POST_MEMBERSHIP_EVENT_PERSISTED_KEY);
  } catch {
    /* ignore blocked storage */
  }
}

export function hasPendingExplorerMembershipEventCombo(): boolean {
  try {
    return Boolean(getExplorerPostMembershipEventCombo());
  } catch {
    return false;
  }
}

/**
 * Clears explorer wizard, event combo, and related tab session keys for this browser tab only.
 * Call on logout (and when another tab removes auth) so stale `explorer_eventId` / pending
 * onboarding cannot attach to the next sign-in in this tab.
 */
export function clearExplorerSessionOnLogout(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    const hadCombo = !!sessionStorage.getItem(EXPLORER_POST_MEMBERSHIP_EVENT_KEY)?.trim();
    sessionStorage.removeItem(EVENT_ID);
    sessionStorage.removeItem(EVENT_CODE);
    sessionStorage.removeItem(FLOW);
    sessionStorage.removeItem(LEAD);
    sessionStorage.removeItem(PENDING);
    sessionStorage.removeItem(LOCAL_PROFILE);
    sessionStorage.removeItem(STEP2_PERSISTED);
    sessionStorage.removeItem(EXPLORER_PENDING_SUCCESS_KEY);
    sessionStorage.removeItem(EXPLORER_POST_MEMBERSHIP_EVENT_KEY);
    sessionStorage.removeItem(REGISTRATION_SHOW_CONFIRMATION_KEY);
    sessionStorage.removeItem(EXPLORER_EVENT_PAYMENT_DONE_KEY);
    /** Membership checkout 3DS resume — do not carry across users in this tab. */
    sessionStorage.removeItem("registration_3ds_resume");
    if (hadCombo) notifyExplorerComboRegisterChromeChanged();
  } catch {
    /* ignore private mode / blocked storage */
  }
}

/** Call after setting/clearing `EXPLORER_POST_MEMBERSHIP_EVENT_KEY` so Layout can show/hide chrome. */
export function notifyExplorerComboRegisterChromeChanged(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(EXPLORER_COMBO_REGISTER_CHROME_EVENT));
  }
}
